import { apiDirectoryService, apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import { objectsRHandler, DEFAULT_PARAMS } from '@/utils/objectsRHandler';
import { getApiData, API_TYPES } from '@/utils/modelsApi';
import { ROLES } from '@/constants/roles';

import Event from '../list/event';
import Participant from '../list/participant';
import EventAttribute from '../list/event-attribute';
import { getDownloadLinkByType } from '@/helpers/files';
import { TYPE } from '@/constants/filesExtensions';
import EventDocument from '../list/event-document';
import BaseModel from '../list/base';

const PRIVATE_ROLES = [ROLES.organization, ROLES.event_manager, ROLES.admin];

const API_DATA = {
  [API_TYPES.private]: {
    path: 'events',
    get objects() {
      const { path } = this;
      return {
        ...objectsRHandler(path, Event),

        getMembers({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/members`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        getMembersCached({ args = [], params } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.getCached(`/${path}/${id}/members`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new Participant(i, 'members')),
            }),
            (error) => ({ error }),
          );
        },
        expelMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/expel`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        rejectMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/reject`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        confirmMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/confirm`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        considerMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/consider`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        incorrectDocumentsMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/incorrect-documents`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        attachGroupMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/set-group`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        detachGroupMember({ args = [], data } = DEFAULT_PARAMS) {
          const [id] = args;
          return rHandler(
            apiService.post(`/${path}/${id}/members/detach-group`, data),
            (response) => ({ response }),
            (error) => ({ error }),
          );
        },
        getAttributes({ args = [], params } = DEFAULT_PARAMS) { // search[nomination_id]
          const [id] = args;
          return rHandler(
            apiService.get(`/${path}/${id}/attributes`, params),
            (response) => ({
              response,
              items: response.data.data.map((i) => new EventAttribute(i, 'event-global-attributes')),
            }),
            (error) => ({ error }),
          );
        },
      };
    },
  },
  [API_TYPES.public]: {
    path: 'public/events',
    get objects() {
      const { path } = this;
      return {
        ...objectsRHandler(path, Event),
      };
    },
  },
};

export const EventManager = {
  get objects() {
    return { ...getApiData(API_DATA, PRIVATE_ROLES).objects };
  },
  get attributes() {
    const path = 'event-global-attributes';
    return {
      ...objectsRHandler(path, EventAttribute),
      massDelete({ data } = DEFAULT_PARAMS) { // { attribute_ids: [1671] }
        return rHandler(
          apiService.post(`/${path}/mass-delete`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      attach({ args = [], data } = DEFAULT_PARAMS) { // attach: [{ event_id: 9822, nomination_id: 18972, options: { required: false } }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/attach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      detach({ args = [], data } = DEFAULT_PARAMS) { // detach: [{ event_id: 9822, nomination_id: 18972 }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/detach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      getTemplateForList({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiService.getCached('/event-global-attributes/import-template', params),
          (response) => ({
            response,
            item: response.data.data, // { file: Base64-String }
            get objectUrl() {
              const { item } = this;
              if (!item?.file) return null;
              return getDownloadLinkByType(item.file, 'Пример', TYPE.xlsx);
            },
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  get attributesValues() {
    const path = 'event-attribute-values';
    return {
      ...objectsRHandler(path, BaseModel),
    };
  },
  get documents() {
    const path = 'event-directory-document';
    return {
      ...objectsRHandler(path, EventDocument),
      attach({ args = [], data } = DEFAULT_PARAMS) { // attach: [{ event_id: 9822, nomination_id: 18972, options: { text: 1234 } }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/attach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      detach({ args = [], data } = DEFAULT_PARAMS) { // detach: [{ event_id: 9822, nomination_id: 18972 }]
        const [id] = args;
        return rHandler(
          apiService.post(`/${path}/${id}/detach`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      getDirectories({ params } = DEFAULT_PARAMS) {
        return rHandler(
          apiDirectoryService.getCached('/directory-event-documents', params),
          (response) => ({
            response,
            items: response.data.data,
          }),
          (error) => ({ error }),
        );
      },
    };
  },
};
